import React from "react";
import {
  Box,
  Card,
  Chip,
  Stack,
  Typography,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CampaignOutlined,
  GroupsOutlined,
  AutoAwesomeOutlined,
  LocalOfferOutlined,
  TrendingUpOutlined,
  MailOutline,
} from "@mui/icons-material";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";

function Stat({ label, value, hint, color }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 140,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5, color: color || "text.primary" }}>
        {value ?? "—"}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {hint}
        </Typography>
      ) : null}
    </Card>
  );
}

/**
 * Marketing hub overview — growth KPIs and quick actions.
 */
export default function MarketingOverview({ lang, onNavigate }) {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [stats, setStats] = React.useState(null);
  const [campaigns, setCampaigns] = React.useState([]);
  const [journeys, setJourneys] = React.useState([]);
  const [promos, setPromos] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      const [a, c, j, p] = await Promise.all([
        hitAxios({
          path: "/api/admin/marketing/audience/stats",
          post: false,
          admin: true,
          showSnackbar: false,
        }),
        hitAxios({
          path: "/api/admin/marketing/campaigns",
          post: false,
          admin: true,
          showSnackbar: false,
        }),
        hitAxios({
          path: "/api/admin/marketing/journeys",
          post: false,
          admin: true,
          showSnackbar: false,
        }),
        hitAxios({
          path: "/api/admin/marketing/promos",
          post: false,
          admin: true,
          showSnackbar: false,
        }),
      ]);
      if (a?.data?.success) setStats(a.data.data);
      if (c?.data?.success) setCampaigns(c.data.data || []);
      if (j?.data?.success) setJourneys(j.data.data || []);
      if (p?.data?.success) setPromos(p.data.data || []);
    })();
  }, [hitAxios]);

  const sentTotal = campaigns.reduce((s, r) => s + (Number(r.sent_count) || 0), 0);
  const activeJourneys = journeys.filter((j) => j.status === "active").length;
  const activeEnrollments = journeys.reduce(
    (s, j) => s + (Number(j.active_enrollments) || 0),
    0,
  );
  const activePromos = promos.filter((p) => p.active).length;

  const go = (page) => {
    if (typeof onNavigate === "function") onNavigate(page);
    else {
      const url = new URL(window.location.href);
      url.searchParams.set("page", page);
      window.history.pushState({}, "", url.toString());
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  const actions = [
    {
      title: "Email campaigns",
      desc: "Send targeted one-time blasts to opted-in users",
      page: "email-campaigns",
      icon: <MailOutline />,
    },
    {
      title: "Automations",
      desc: "Day 0 → Day N onboarding drips after signup",
      page: "marketing-automations",
      icon: <AutoAwesomeOutlined />,
    },
    {
      title: "Audiences",
      desc: "See who you can reach and why counts differ",
      page: "marketing-audiences",
      icon: <GroupsOutlined />,
    },
    {
      title: "Promo codes",
      desc: "Credit coupons for ads, partners, and win-backs",
      page: "marketing-promos",
      icon: <LocalOfferOutlined />,
    },
    {
      title: "Affiliate",
      desc: "Referral links and invite rewards",
      page: "affiliate",
      icon: <TrendingUpOutlined />,
    },
    {
      title: "Leads",
      desc: "Contact form and inbound leads",
      page: "leads",
      icon: <CampaignOutlined />,
    },
  ];

  return (
    <Box>
      <PageHeader
        title={lang?.marketingOverview || "Marketing"}
        subtitle="Grow signups, activate new users, and convert free → paid"
        icon={CampaignOutlined}
      />

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
        <Stat
          label="Reachable (opt-in)"
          value={stats?.marketing_opt_in}
          hint={`${stats?.active_users ?? "—"} active · ${stats?.marketing_opted_out ?? 0} opted out`}
          color={theme.palette.primary.main}
        />
        <Stat label="Emails sent (campaigns)" value={sentTotal} />
        <Stat
          label="Live automations"
          value={activeJourneys}
          hint={`${activeEnrollments} active enrollments`}
        />
        <Stat label="Active promo codes" value={activePromos} />
        <Stat label="Signed up (7d)" value={stats?.signed_up_7d} color="#059669" />
        <Stat label="Signed up (30d)" value={stats?.signed_up_30d} />
      </Stack>

      <Typography variant="subtitle1" fontWeight={800} mb={1.5}>
        Playbooks
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
        {actions.map((a) => (
          <Card
            key={a.page}
            elevation={0}
            sx={{
              width: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(33.33% - 10px)" },
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              cursor: "pointer",
              transition: "border-color .15s, box-shadow .15s",
              "&:hover": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
                boxShadow: `0 8px 24px ${alpha("#000", 0.06)}`,
              },
            }}
            onClick={() => go(a.page)}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              >
                {a.icon}
              </Box>
              <Box>
                <Typography fontWeight={800}>{a.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {a.desc}
                </Typography>
              </Box>
            </Stack>
          </Card>
        ))}
      </Stack>

      <AlertTips />
    </Box>
  );
}

function AlertTips() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        mt: 3,
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.info.main, 0.04),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Chip size="small" label="Best practice" color="info" />
        <Typography fontWeight={800}>Weekly growth loop</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" component="div">
        <ol style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.7 }}>
          <li>Keep a signup automation active (Day 0–2) so every new user gets onboarded.</li>
          <li>Blast opted-in free users weekly with one clear CTA (Image Studio or Video Studio).</li>
          <li>Run promo codes in ads / partner deals; track redemptions here.</li>
          <li>Push Affiliate invites so creators bring the next cohort.</li>
        </ol>
      </Typography>
      <Button
        size="small"
        sx={{ mt: 1.5, textTransform: "none", fontWeight: 700 }}
        onClick={() => {
          const url = new URL(window.location.href);
          url.searchParams.set("page", "marketing-automations");
          window.history.pushState({}, "", url.toString());
          window.dispatchEvent(new PopStateEvent("popstate"));
        }}
      >
        Open automations →
      </Button>
    </Box>
  );
}
